<script setup lang="ts">
// Inlined original CreateAdmin.vue
import { onMounted, ref, watch } from 'vue'
import { signUpAdmin, confirmSignUpAdmin } from '@/services/authService'
import { useDisplay } from 'vuetify'
import router from '@/router'
import { SendAdminName } from '@/api/accountsAdmin'

const { xs } = useDisplay()

const email = ref('')
const username = ref('')
const password = ref('')
const newPassword = ref('')
const isEmailSent = ref(false)
const message = ref('')
const snackbar = ref(false)
const searchQuery = ref('')
const showPassword = ref(false)
const loading = ref(false)
const showPasswordConfirm = ref(false)
const confirmPassword = ref('')
const passwordError = ref<string | null>(null)

const handleSignUp = async () => {
  loading.value = true
  try {
    if (!username.value || !email.value || !password.value || !confirmPassword) {
      message.value = '❌ Please fill all the fields'
      snackbar.value = true
      loading.value = false
      return
    }
    if (!email.value.endsWith('@jazel.com') && !email.value.endsWith('@10thdegree.com')) {
      message.value = '❌ Email must be a @jazel.com or @10thdegree.com address.'
      snackbar.value = true
      loading.value = false
      return
    }
    if (confirmPassword.value !== password.value) {
      message.value = '❌ New password and confirmation do not match.'
      snackbar.value = true
      loading.value = false
      return
    }
    await signUpAdmin(username.value, email.value, password.value)
    message.value = '✅ Verification email sent. Please click the link in your inbox to confirm.'
    await SendAdminName(username?.value, email?.value)
    isEmailSent.value = true
    snackbar.value = true
    loading.value = false
  } catch (error: any) {
    message.value = error.message || '❌ Signup failed.'
    snackbar.value = true
    loading.value = false
  }
}
const goBackAfterEmail = () => {
  router.push('/company/login')
}
const required = (v: string) => !!v || 'This field is required'
const emailRules = [
  (v: string) => !!v || 'Email is required',
  (v: string) => /.+@.+\..+/.test(v) || 'Enter a valid email',
  (v: string) => v.endsWith('@jazel.com') || v.endsWith('@10thdegree.com') || 'Email must be a @jazel.com or @10thdegree.com address',
]
</script>

<template>
  <v-container fluid class="page-wrapper">
    <div style="position: absolute; right: 20px; top: 20px">
      <v-btn color="#3677D3" class="login-btn" @click="router.back()" aria-label="Go back">
        <v-icon>mdi-arrow-left</v-icon>
        &nbsp; GO BACK
      </v-btn>
    </div>
    <div class="auth-container">
      <h2 class="brand">SodaSoft.io</h2>
      <v-card class="auth-card" :max-width="xs ? '100%' : 440">
        <div class="text-center mb-8">
          <h1 class="welcome">Sign Up Company</h1>
          <p class="subtitle">Sign up using your Company Email</p>
        </div>
        <v-form @submit.prevent="isEmailSent ? goBackAfterEmail() : handleSignUp()">
          <div class="input-group">
            <input
              type="text"
              v-if="!isEmailSent"
              placeholder="Username"
              v-model="username"
              class="input"
              outlined
              :rules="[required]"
            />
            <input
              type="email"
              v-if="!isEmailSent"
              placeholder="Email"
              v-model="email"
              class="input"
              outlined
              :rules="[required]"
            />
            <div class="password-wrapper" v-if="!isEmailSent">
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                placeholder="Password"
                :class="['input', { 'is-invalid': passwordError }]"
              />
              <button type="button" class="eye-btn" @click="showPassword = !showPassword">
                <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
              </button>
            </div>
            <div class="password-wrapper" v-if="!isEmailSent">
              <input
                :type="showPasswordConfirm ? 'text' : 'password'"
                v-model="confirmPassword"
                placeholder="Confirm Password"
                :class="['input', { 'is-invalid': passwordError }]"
              />
              <button
                type="button"
                class="eye-btn"
                @click="showPasswordConfirm = !showPasswordConfirm"
              >
                <i :class="showPasswordConfirm ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
              </button>
            </div>
          </div>
          <div v-if="isEmailSent" class="fineprint">
            We sent a verification link to
            <strong>{{ email }}</strong>
            . After verifying, you can log in.
          </div>
          <v-btn
            block
            :color="isEmailSent ? 'green' : '#3D7FD8'"
            :disabled="loading"
            type="submit"
            height="48"
            class="login-btn mt-5"
          >
            {{ isEmailSent ? 'Back to Login' : 'Sign Up' }}
          </v-btn>
        </v-form>
      </v-card>
      <v-snackbar v-model="snackbar" color="info" :timeout="3000">
        {{ message }}
        <template v-slot:action="{ attrs }">
          <v-btn color="white" text v-bind="attrs" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </div>
  </v-container>
</template>

<style scoped>
/* Page wrapper */
.page-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #cacaca;
  padding: 24px 16px;
}

.auth-container {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Brand */
.brand {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 28px;
  text-align: left;
  align-self: flex-start;
}

/* Card */
.auth-card {
  width: 100%;
  border-radius: 16px;
  padding: 44px 36px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Headings */
.welcome {
  font-size: 36px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.2;
}

.subtitle {
  font-size: 14px;
  color: #5a5a5a;
  margin-top: 8px;
  line-height: 1.5;
}

/* Input group */
.input-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 4px;
}

.input {
  width: 100%;
  height: 48px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0 16px;
  font-size: 14px;
  color: #1a1a1a;
  background: white;
  transition: border-color 0.2s;
}

.input:focus {
  outline: none;
  border-color: #4a90e2;
}

.input::placeholder {
  color: #aaaaaa;
}

.password-wrapper {
  position: relative;
  width: 100%;
}

.password-wrapper .input {
  padding-right: 48px;
}

.eye-btn {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 4px;
}

.eye-btn:hover {
  color: #333;
}

.eye-btn .mdi {
  font-size: 20px;
}

/* Button */
.login-btn {
  border-radius: 8px;
  text-transform: none;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0;
  box-shadow: none !important;
}

/* Fine print */
.fineprint {
  font-size: 13px;
  color: #5a5a5a;
  text-align: center;
  margin-top: 12px;
  line-height: 1.5;
}

/* Autofill fix */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px white inset;
  -webkit-text-fill-color: #1a1a1a;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
